import React, { useRef } from 'react';
import { Button, InlineIcon } from '@spark-labs/avocado-ui';
import BubbleTooltip from '@go-client/components/BubbleUI/BubbleTooltip';
import { toast } from '@go-client/components/UIkit/Toast/Toast';
import {
  acceptFor,
  describeUploadRule,
  getUploadError,
  UploadRule,
} from '@go-shared/lib/uploads';

interface UploadButtonProps {
  onFilesChange: (files: FileList) => void;
  accept?: string;
  // Restricts the selectable files and rejects the ones that do not match it before uploading
  uploadRule?: UploadRule;
  // Shows a "?" tooltip with the allowed types and size limit. Requires uploadRule.
  // 'inside' passes the icon to render so the trigger can draw it within itself
  hintPosition?: 'top' | 'bottom' | 'right' | 'inside';
  render?: (upload: () => void, hint?: React.ReactNode) => JSX.Element;
  singleUpload?: boolean;
}

export const UploadButton: React.FC<UploadButtonProps> = ({
  children,
  onFilesChange,
  accept,
  uploadRule,
  hintPosition,
  render,
  singleUpload,
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length && inputRef.current) {
      const error =
        uploadRule &&
        Array.from(e.target.files)
          .map(file => getUploadError(uploadRule, file))
          .find(Boolean);

      if (error) {
        void toast.error(error);
      } else {
        onFilesChange(e.target.files);
      }
      /**
       * If this is enabled, you won't be able to get a filelist, you can only upload one
       * file, the input resets after the file is sent via props handler
       * Rejected files are cleared too, so they can be selected again after fixing them
       */
      if (singleUpload || error) {
        inputRef.current.value = '';
      }
    }
  };

  const handleUpload = () => {
    if (inputRef.current) {
      const ref = inputRef.current;
      ref.click();
    }
  };

  const hintIcon = uploadRule && (
    <BubbleTooltip text={describeUploadRule(uploadRule)}>
      <InlineIcon name="question-circle" />
    </BubbleTooltip>
  );

  const renderChildren = () => {
    if (render) {
      return render(handleUpload, hintPosition === 'inside' ? hintIcon : undefined);
    } else {
      return (
        <Button className="w-full bg-blue-dark py-2 px-3 text-white" onClick={handleUpload}>
          {children}
        </Button>
      );
    }
  };
  const content = (
    <label htmlFor="upload-button">
      <input
        type="file"
        ref={inputRef}
        accept={accept ?? (uploadRule && acceptFor(uploadRule))}
        style={{ display: 'none' }}
        onChange={handleChange}
      />
      {renderChildren()}
    </label>
  );

  const hint = uploadRule && hintPosition && hintPosition !== 'inside' && (
    <div
      className={`text-xs italic text-smoke ${
        hintPosition === 'top' ? 'mb-1' : hintPosition === 'right' ? 'ml-3' : 'mt-1'
      }`}
    >
      {hintIcon}
    </div>
  );

  if (!hint) return content;

  if (hintPosition === 'right') {
    return (
      <div className="flex items-center">
        {content}
        {hint}
      </div>
    );
  }

  return (
    <div>
      {hintPosition === 'top' && hint}
      {content}
      {hintPosition === 'bottom' && hint}
    </div>
  );
};
